import { useEffect, useRef, useState } from "react"
import { router } from "@inertiajs/react"
import { Paperclip, X, FileText, Lock, Send, Smile, Mail } from "lucide-react"
import data from "@emoji-mart/data"
import Picker from "@emoji-mart/react"
import { cn } from "@/lib/cn"
import { MarkdownEditor, type MarkdownEditorHandle } from "@/components/domain/markdown-editor"

type Props = {
  ticketId: string
  disabled?: boolean
}

type ComposerMode = "reply" | "note"

// Mirrors Agents::AttachmentPolicy on the server. Kept inline rather than
// fetched because the composer doesn't have a status endpoint and these
// limits are stable.
const MAX_FILE_BYTES = 10 * 1024 * 1024
const ALLOWED_MIME = new Set([
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
  "video/mp4",
  "application/pdf",
])
const ACCEPT_ATTR = Array.from(ALLOWED_MIME).join(",")

// Reply box at the bottom of the conversation thread. Two cards stacked:
//   1. Composer section — pill-group tabs + ⌘K hint + inner colored card
//      (composer-reply or composer-note) holding the textarea + toolbar
//      + send button + the mode-aware status line under the inner card.
//   2. Pro-tip card — separate sibling card with the / and @ hints.
//
// Posts to /tickets/:id/reply (public, with files) or /tickets/:id/add_note
// (text-only). Drag-and-drop and the paperclip button both feed the same
// pendingFiles[] array; previews render above the textarea.
export function TicketComposer({ ticketId, disabled = false }: Props) {
  const [mode, setMode]                 = useState<ComposerMode>("reply")
  // `body` mirrors the editor's current markdown so the send button can
  // disable when there's nothing to submit. The editor itself is the
  // source of truth — we read via editorRef on submit.
  const [body, setBody]                 = useState("")
  const [submitting, setSubmitting]     = useState(false)
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const [fileError, setFileError]       = useState<string | null>(null)
  const [dragHover, setDragHover]       = useState(false)
  const [emojiOpen, setEmojiOpen]       = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const editorRef    = useRef<MarkdownEditorHandle>(null)
  const emojiAnchorRef = useRef<HTMLDivElement | null>(null)
  const isNote = mode === "note"

  // Emoji popover dismissal — close on outside click and Escape so the
  // operator can dismiss without picking. The picker itself handles its
  // own internal interactions (search input, category nav).
  useEffect(() => {
    if (!emojiOpen) return
    function onClick(e: MouseEvent) {
      if (!emojiAnchorRef.current) return
      if (emojiAnchorRef.current.contains(e.target as Node)) return
      setEmojiOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setEmojiOpen(false)
    }
    window.addEventListener("mousedown", onClick)
    window.addEventListener("keydown", onKey)
    return () => {
      window.removeEventListener("mousedown", onClick)
      window.removeEventListener("keydown", onKey)
    }
  }, [emojiOpen])

  // ⌘K / Ctrl+K toggles the mode without losing focus on the textarea.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault()
        setMode((m) => (m === "reply" ? "note" : "reply"))
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [])

  // Typing-indicator throttle. Pings POST /tickets/:id/typing at most
  // once per 500ms while the operator is typing, then a `state=stop`
  // ping ~3s after typing pauses so the indicator clears on viewers.
  const lastTypingPingAtRef = useRef(0)
  const stopTypingTimerRef  = useRef<number | null>(null)
  function pingTyping(state: "start" | "stop") {
    const headers: Record<string, string> = { "Content-Type": "application/json" }
    const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
    if (csrf) headers["X-CSRF-Token"] = csrf
    fetch(`/tickets/${ticketId}/typing`, {
      method:  "POST",
      headers,
      body:    JSON.stringify({ state }),
      credentials: "same-origin",
    }).catch(() => { /* indicator is UX polish, swallow failures */ })
  }
  function noteKeystroke() {
    const now = Date.now()
    if (now - lastTypingPingAtRef.current >= 500) {
      lastTypingPingAtRef.current = now
      pingTyping("start")
    }
    if (stopTypingTimerRef.current != null) window.clearTimeout(stopTypingTimerRef.current)
    stopTypingTimerRef.current = window.setTimeout(() => {
      lastTypingPingAtRef.current = 0
      stopTypingTimerRef.current = null
      pingTyping("stop")
    }, 3000)
  }
  useEffect(() => {
    return () => {
      if (stopTypingTimerRef.current != null) {
        window.clearTimeout(stopTypingTimerRef.current)
        if (lastTypingPingAtRef.current > 0) pingTyping("stop")
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function addFiles(incoming: File[]) {
    if (incoming.length === 0) return
    const accepted: File[] = []
    for (const f of incoming) {
      if (!ALLOWED_MIME.has(f.type)) {
        setFileError(`${f.name}: unsupported file type (${f.type || "unknown"}).`)
        continue
      }
      if (f.size > MAX_FILE_BYTES) {
        setFileError(`${f.name}: file too large (max 10MB).`)
        continue
      }
      accepted.push(f)
    }
    if (accepted.length > 0) {
      setFileError(null)
      setPendingFiles((prev) => [...prev, ...accepted])
    }
  }

  function removeFile(idx: number) {
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx))
  }

  function submit() {
    if (submitting || disabled) return
    // Read the current markdown from the editor — it's the source of
    // truth; `body` state lags by one onUpdate tick.
    const markdown = editorRef.current?.getMarkdown() ?? body
    if (!markdown.trim() && pendingFiles.length === 0) return
    if (stopTypingTimerRef.current != null) {
      window.clearTimeout(stopTypingTimerRef.current)
      stopTypingTimerRef.current = null
    }
    if (lastTypingPingAtRef.current > 0) {
      lastTypingPingAtRef.current = 0
      pingTyping("stop")
    }
    setSubmitting(true)
    const clearAndReset = () => {
      editorRef.current?.clear()
      setBody("")
      setPendingFiles([])
      setFileError(null)
    }
    if (isNote) {
      // Internal notes: text-only — keeps the visibility-leak surface
      // narrow and the controller path cleaner.
      router.post(`/tickets/${ticketId}/add_note`, { body: markdown }, {
        preserveScroll: true,
        onSuccess:  clearAndReset,
        onFinish:   () => setSubmitting(false),
      })
      return
    }
    const payload: Record<string, string | File[]> = { body: markdown }
    if (pendingFiles.length > 0) payload.files = pendingFiles
    router.post(`/tickets/${ticketId}/reply`, payload, {
      preserveScroll: true,
      forceFormData:  pendingFiles.length > 0,
      onSuccess:  clearAndReset,
      onFinish:   () => setSubmitting(false),
    })
  }

  function send(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    submit()
  }

  function onDrop(e: React.DragEvent<HTMLFormElement>) {
    e.preventDefault()
    setDragHover(false)
    if (disabled) return
    addFiles(Array.from(e.dataTransfer.files))
  }

  const sendDisabled = disabled || submitting || (!body.trim() && (isNote || pendingFiles.length === 0))

  return (
    <div className="flex flex-col gap-3">
      <form
        onSubmit={send}
        onDragOver={(e) => { e.preventDefault(); if (!disabled && !isNote) setDragHover(true) }}
        onDragLeave={() => setDragHover(false)}
        onDrop={onDrop}
        className={cn(
          "rounded-2xl border border-white/10 bg-app-panel2/80 p-4 transition",
          dragHover && !isNote && "bg-brand-red/5",
        )}
      >
        {/* Tab pill-group + ⌘K hint. The hint is desktop-only — mobile
            doesn't have a meta key — so it hides below `sm:`. The pill
            group can wrap to the next line on very narrow viewports. */}
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex rounded-xl border border-white/10 bg-black/20 p-1">
            <PillTab
              active={!isNote}
              onClick={() => setMode("reply")}
              tone="red"
            >
              <Send size={13} strokeWidth={2.5} className="mr-1.5" /> Reply
            </PillTab>
            <PillTab
              active={isNote}
              onClick={() => setMode("note")}
              tone="gold"
            >
              <Lock size={13} strokeWidth={2.5} className="mr-1.5" /> Internal note
            </PillTab>
          </div>
          <div className="hidden text-xs text-app-subtle sm:block">
            <span className="rounded-md border border-white/10 bg-white/5 px-2 py-1 font-mono text-app-text">⌘K</span>
            <span className="ml-1">to switch</span>
          </div>
        </div>

        {/* Pending file previews */}
        {pendingFiles.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-2">
            {pendingFiles.map((f, i) => (
              <PendingChip key={`${f.name}-${i}`} file={f} onRemove={() => removeFile(i)} />
            ))}
          </div>
        )}
        {fileError && (
          <div className="mb-2 rounded-control border border-status-error/30 bg-status-error/10 px-2 py-1 text-xs text-status-error">
            {fileError}
          </div>
        )}

        {/* Inner colored card — textarea + toolbar + send button */}
        <div className={cn(
          "rounded-xl border p-4",
          isNote
            ? "composer-note bg-[#1a1408]/40"
            : "composer-reply bg-black/20",
        )}>
          <MarkdownEditor
            ref={editorRef}
            placeholder={
              disabled
                ? "Conversation closed — reopen to reply."
                : isNote
                  ? "Add an internal note…"
                  : "Write your reply…"
            }
            disabled={disabled || submitting}
            toneClass={isNote ? "note" : "reply"}
            onChange={(md) => {
              setBody(md)
              if (!isNote) noteKeystroke()
            }}
            onSubmit={submit}
          />
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPT_ATTR}
            className="hidden"
            onChange={(e) => {
              addFiles(Array.from(e.target.files ?? []))
              e.target.value = "" // allow re-selecting the same file
            }}
          />

          {/* Toolbar — wraps on very narrow viewports so the attach +
              emoji icons don't push the send button off-screen. flex-wrap
              + a ml-auto on the send button keeps send right-aligned even
              after a wrap. */}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-3 text-app-muted">
              <ToolbarIconButton
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled || submitting || isNote}
                title={isNote
                  ? "Attachments not supported on internal notes — use a reply"
                  : "Attach files (images, mp4, pdf — 10MB max each)"}
                label="Attach"
              >
                <Paperclip size={16} strokeWidth={2} />
              </ToolbarIconButton>
              <div ref={emojiAnchorRef} className="relative">
                <ToolbarIconButton
                  onClick={() => setEmojiOpen((v) => !v)}
                  disabled={disabled}
                  title="Emoji"
                  label="Emoji"
                >
                  <Smile size={16} strokeWidth={2} />
                </ToolbarIconButton>
                {emojiOpen && (
                  // Picker popover anchored to the smile button. Pops UP
                  // (bottom-full) so it doesn't get clipped by the card.
                  <div className="absolute bottom-full left-0 z-50 mb-2">
                    <Picker
                      data={data}
                      theme="dark"
                      previewPosition="none"
                      navPosition="top"
                      perLine={8}
                      maxFrequentRows={2}
                      onEmojiSelect={(emoji: { native: string }) => {
                        editorRef.current?.insertText(emoji.native)
                        setEmojiOpen(false)
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
            <button
              type="submit"
              disabled={sendDisabled}
              className={cn(
                "ml-auto inline-flex min-h-12 items-center gap-1.5 rounded-xl px-5 py-3 text-sm font-semibold transition disabled:opacity-50 disabled:pointer-events-none",
                isNote ? "btn-gold" : "btn-red text-white",
              )}
            >
              {isNote
                ? <Lock size={14} strokeWidth={2.5} />
                : <Send size={14} strokeWidth={2.5} />
              }
              {submitting
                ? (isNote ? "Saving…" : "Sending…")
                : (isNote ? "Add note" : "Send reply")}
            </button>
          </div>
        </div>

        {/* Mode-aware status line */}
        <div className={cn(
          "mt-3 flex items-center gap-2 text-sm",
          isNote ? "text-vip-goldSoft" : "text-app-muted",
        )}>
          {isNote
            ? <Lock size={14} strokeWidth={2.5} />
            : <Mail size={14} strokeWidth={2} />
          }
          <span>
            {isNote
              ? "Internal notes are visible to your team and never sent to the customer."
              : "Replies are sent to the customer via email."
            }
          </span>
        </div>
      </form>

      {/* Pro tip — separate card below the composer */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-app-muted">
        💡 <span className="font-medium text-app-text">Pro tip:</span>{" "}
        Use <span className="font-mono text-app-text">/</span> to insert a canned response.
      </div>
    </div>
  )
}

function PillTab({ active, onClick, tone, children }: {
  active: boolean
  onClick: () => void
  tone: "red" | "gold"
  children: React.ReactNode
}) {
  // The default browser focus outline is blue — on dark surfaces it
  // clashes with the tab's own red/gold border. Kill it and replace
  // with a tone-matched focus-visible ring so the only outline ever
  // shown matches the mode the tab represents.
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center rounded-lg px-4 py-2 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-0",
        tone === "gold" ? "focus-visible:ring-vip-gold" : "focus-visible:ring-brand-red",
        active
          ? tone === "gold"
            ? "border border-vip-gold bg-vip-gold/15 text-vip-goldSoft"
            : "border border-brand-red bg-brand-red/15 text-white"
          : tone === "gold"
            ? "text-app-muted hover:text-vip-goldSoft"
            : "text-app-muted hover:text-white",
      )}
    >
      {children}
    </button>
  )
}

function ToolbarIconButton({ onClick, disabled, title, label, children }: {
  onClick: () => void
  disabled?: boolean
  title: string
  label: string
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={label}
      className="rounded-lg p-2 text-app-muted transition hover:bg-white/5 hover:text-white disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-app-muted"
    >
      {children}
    </button>
  )
}

function PendingChip({ file, onRemove }: { file: File; onRemove: () => void }) {
  const isImage = file.type.startsWith("image/")
  const isVideo = file.type.startsWith("video/")
  const sizeKb  = (file.size / 1024).toFixed(1)
  const previewUrl = (isImage || isVideo) ? URL.createObjectURL(file) : null
  useEffect(() => {
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl) }
  }, [previewUrl])

  return (
    <div className="group relative flex items-center gap-2 rounded-control border border-white/10 bg-app-panel2 px-2 py-1">
      {isImage && previewUrl && (
        <img src={previewUrl} alt={file.name} className="h-8 w-8 rounded-sm object-cover" />
      )}
      {isVideo && previewUrl && (
        <video src={previewUrl} className="h-8 w-8 rounded-sm object-cover" muted />
      )}
      {!isImage && !isVideo && <FileText size={14} className="text-app-muted" />}
      <span className="max-w-[180px] truncate text-xs text-app-text" title={file.name}>
        {file.name}
      </span>
      <span className="text-[10px] text-app-subtle">{sizeKb} KB</span>
      <button
        type="button"
        onClick={onRemove}
        className="ml-1 rounded-full p-0.5 text-app-muted hover:bg-white/10 hover:text-status-error"
        title="Remove"
        aria-label={`Remove ${file.name}`}
      >
        <X size={12} />
      </button>
    </div>
  )
}
